import { ScriptProps } from 'next/script';

export type ScriptTagModel = {
  hid: string;
  src: string;
  [key: string]: any;
};

export type VanillaScriptsModel = {
  hid: string;
  innerHTML: string;
  [key: string]: any;
};

export type ParsedScriptsModel = {
  scripts: ScriptTagModel[];
  vanillaScripts: VanillaScriptsModel | null;
};

export function parseCMSScripts(scripts: string): ParsedScriptsModel | null {
  if (!scripts || scripts === '') return null;

  const scriptSrcs: ParsedScriptsModel = {
    scripts: [],
    vanillaScripts: null,
  };

  const scriptRegex = /<script\b[^>]*\bsrc\s*=\s*["'][^"']+["'][^>]*>/gi;

  // Regex to grab attributes within a tag
  const attrRegex = /([^\s=]+)\s*=\s*["']([^"']*)["']/gi;

  let scriptMatch;

  while ((scriptMatch = scriptRegex.exec(scripts)) !== null) {
    const tag = scriptMatch[0]; // full <script ...> tag

    const attrs: any = {};
    let attrMatch;
    while ((attrMatch = attrRegex.exec(tag)) !== null) {
      const name = attrMatch[1];
      const value = attrMatch[2];
      attrs[name] = value;
    }

    scriptSrcs.scripts.push({
      hid: attrs.src.replace(/[^a-zA-Z]/g, ''),
      ...attrs,
    });
  }

  const vanillaScripts = scripts
    ?.split(/<\/script>/g)
    ?.map((s) => {
      if (!s || s === '') return null;
      return s.replace(/<script[^>]*>/g, '').replace(/<\/script>/g, '');
    })
    .filter((s) => s !== null);

  if (vanillaScripts) {
    scriptSrcs.vanillaScripts = {
      hid: 'headscripts',
      defer: true,
      innerHTML: vanillaScripts.join('\n'),
    };
  }

  return scriptSrcs;
}

export function loadScript(
  src: string,
  {
    id,
    attrs = {},
    check,
    timeout = 15000,
  }: {
    id: string;
    attrs?: Record<string, string>;
    check?: () => boolean;
    timeout?: number;
  },
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (typeof window === 'undefined') return resolve(); // SSR no-op

    if (typeof check === 'function' && check()) return resolve();

    const existing: HTMLScriptElement | null = id
      ? (document.getElementById(id) as HTMLScriptElement | null)
      : document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);

    if (existing) {
      // already added; resolve when it fires load (or immediately if marked)
      if (existing.dataset.loaded === 'true') return resolve();
      const onLoad = () => resolve();
      const onError = (e: Event) => reject(e);
      existing.addEventListener('load', onLoad, { once: true });
      existing.addEventListener('error', onError, { once: true });
      return;
    }

    const s = document.createElement('script');
    s.src = src;
    if (id) s.id = id;
    s.defer = true;

    for (const [k, v] of Object.entries(attrs)) {
      s.setAttribute(k, v);
    }

    const onLoad = () => {
      s.dataset.loaded = 'true';
      resolve();
    };
    const onError = (e: Event) => reject(e);

    s.addEventListener('load', onLoad, { once: true });
    s.addEventListener('error', onError, { once: true });

    const t = window.setTimeout(() => {
      s.removeEventListener('load', onLoad);
      s.removeEventListener('error', onError);
      reject(new Error(`Timed out loading ${src}`));
    }, timeout);

    s.addEventListener(
      'load',
      () => {
        window.clearTimeout(t);
      },
      { once: true },
    );
    s.addEventListener(
      'error',
      () => {
        window.clearTimeout(t);
      },
      { once: true },
    );

    document.body!.appendChild(s);
  });
}

export function loadScriptsParallel(scripts: ScriptTagModel[]) {
  const promises = scripts.map((script) => {
    const { src, hid, ...attrs } = script;
    return loadScript(src, { id: hid, attrs });
  });
  return Promise.all(promises);
}

type AllowedScriptAttributes = Pick<
  ScriptProps,
  'id' | 'type' | 'strategy' | 'onLoad' | 'onError' | 'async' | 'defer'
> & {
  [key: `data-${string}`]: any;
};

export function sanitizeScriptAttrs(attrs: Record<string, any> = {}): AllowedScriptAttributes {
  const allowedKeys: (keyof AllowedScriptAttributes)[] = [
    'id',
    'type',
    'strategy',
    'onLoad',
    'onError',
    'async',
    'defer',
  ];

  const result: any = {};

  for (const key in attrs) {
    if (allowedKeys.includes(key as keyof AllowedScriptAttributes) || key.startsWith('data-')) {
      result[key] = attrs[key];
    }
  }

  return result;
}

export function fixTikTokEmbedCode() {
  let isScript = false;
  // Function to remove existing TikTok embed script
  const removeTikTokScript = () => {
    const existingScript = document.querySelectorAll('script[src="https://www.tiktok.com/embed.js"]');
    if (existingScript && existingScript.length > 0) {
      existingScript.forEach((script) => {
        if (!script.getAttribute('data-isfix')) {
          isScript = true;
        }
        script.remove();
      });
    }
  };

  // Function to add TikTok embed script
  const addTikTokScript = () => {
    const script = document.createElement('script');
    script.src = 'https://www.tiktok.com/embed.js';
    script.async = true;
    script.dataset.isfix = 'true';
    document.body.appendChild(script);
  };

  // Remove old script and add new script every time the route changes
  removeTikTokScript();
  if (isScript) {
    addTikTokScript();
  }
}
